import React, { useState, useEffect } from 'react';
import { X, Upload, Plus, Save, IndianRupee, CreditCard, Smartphone, Building, Landmark, AlertCircle, File, DollarSign, Info } from 'lucide-react';
import { fetchVendors } from '../../api/vendorsApi';
import { fetchStaff } from '../../api/staffApi';
import { fetchProducts } from '../../api/productsApi';

const ExpenseForm = ({ expense, onSave, onCancel, onSettlePayment }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [products, setProducts] = useState([]);

  // State for sub-categories that persist across the app
  const [subCategories, setSubCategories] = useState({
    shop_expense: ['rent', 'electricity', 'water', 'internet', 'maintenance', 'others'],
    purchase_invoice: ['groceries', 'packaging', 'utensils', 'cleaning', 'others'],
    staff_payment: ['salary', 'bonus', 'overtime', 'incentive', 'others'],
    others: ['miscellaneous', 'emergency', 'donation', 'gift', 'others']
  });

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    subCategory: '',
    vendorName: '',
    staffName: '',
    description: '',
    paymentMode: 'cash',
    totalAmount: '',
    paidAmount: '',
    pendingAmount: 0,
    status: 'pending',
    transactions: [],
    attachments: [],
    selectedProductIds: []
  });

  const [newSubCategory, setNewSubCategory] = useState('');
  const [showNewSubCategory, setShowNewSubCategory] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPartialPayment, setShowPartialPayment] = useState(false);
  const [partialAmount, setPartialAmount] = useState('');
  const [isPaidExpense, setIsPaidExpense] = useState(false);

  const categories = [
    { value: 'shop_expense', label: 'Shop Expense', icon: Building },
    { value: 'purchase_invoice', label: 'Purchase Invoice', icon: Save },
    { value: 'staff_payment', label: 'Staff Payment', icon: DollarSign },
    { value: 'others', label: 'Others', icon: AlertCircle }
  ];

  const paymentModes = [
    { value: 'cash', label: 'Cash', icon: IndianRupee },
    { value: 'upi', label: 'UPI', icon: Smartphone },
    { value: 'card', label: 'Card', icon: CreditCard },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: Landmark },
    { value: 'net_banking', label: 'Net Banking', icon: Building }
  ];

  // Load sub-categories from localStorage on mount
  useEffect(() => {
    const savedSubCategories = localStorage.getItem('subCategories');
    if (savedSubCategories) {
      setSubCategories(JSON.parse(savedSubCategories));
    }
  }, []);

  // Load suppliers (vendors) and staff for dropdowns
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [vendorsData, staffData, productsData] = await Promise.all([
          fetchVendors().catch(() => []),
          fetchStaff().catch(() => []),
          fetchProducts().catch(() => []),
        ]);
        if (!cancelled) {
          setSuppliers(Array.isArray(vendorsData) ? vendorsData : []);
          setStaffList(Array.isArray(staffData) ? staffData : []);
          setProducts(Array.isArray(productsData) ? productsData : []);
        }
      } catch (_) {
        if (!cancelled) {
          setSuppliers([]);
          setStaffList([]);
          setProducts([]);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Save sub-categories to localStorage when they change
  useEffect(() => {
    localStorage.setItem('subCategories', JSON.stringify(subCategories));
  }, [subCategories]);

  useEffect(() => {
    if (expense) {
      const total = parseFloat(expense.totalAmount) || 0;
      const paid = parseFloat(expense.paidAmount) || 0;
      const pending = Math.max(0, total - paid);
      const isPaid = pending === 0;
      
      setIsPaidExpense(isPaid);
      
      const normalizedAttachments = Array.isArray(expense.attachments)
        ? expense.attachments.map((a) =>
            typeof a === 'object' && a !== null
              ? { name: a.name || a.file_name || a.filename || '', id: a.id }
              : { name: String(a) }
          )
        : [];
      const productIds = Array.isArray(expense.selectedProductIds)
        ? expense.selectedProductIds
        : Array.isArray(expense.product_ids)
          ? expense.product_ids
          : [];
      setFormData({
        ...expense,
        totalAmount: total.toString(),
        paidAmount: paid.toString(),
        pendingAmount: pending,
        status: isPaid ? 'paid' : 'pending',
        attachments: normalizedAttachments,
        selectedProductIds: productIds
      });
      
      if (!isPaid) {
        setPartialAmount(pending.toString());
      }
    } else {
      setIsPaidExpense(false);
    }
  }, [expense]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (formData.category && formData.category !== 'purchase_invoice' && !formData.subCategory) {
      newErrors.subCategory = 'Sub-category is required';
    }
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.paymentMode) newErrors.paymentMode = 'Payment mode is required';
    
    if (!formData.totalAmount || parseFloat(formData.totalAmount) <= 0) {
      newErrors.totalAmount = 'Total amount must be greater than 0';
    }
    
    if (!formData.paidAmount || parseFloat(formData.paidAmount) < 0) {
      newErrors.paidAmount = 'Paid amount cannot be negative';
    }
    
    if (parseFloat(formData.paidAmount) > parseFloat(formData.totalAmount || 0)) {
      newErrors.paidAmount = 'Paid amount cannot exceed total amount';
    }
    
    if (formData.category === 'purchase_invoice' && !formData.vendorName) {
      newErrors.vendorName = 'Vendor name is required';
    }
    
    if (formData.category === 'staff_payment' && !formData.staffName) {
      newErrors.staffName = 'Staff name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    if (name === 'category') {
      setFormData({
        ...formData,
        [name]: value,
        subCategory: '',
        vendorName: '',
        staffName: '',
        selectedProductIds: []
      });
      setNewSubCategory('');
      setShowNewSubCategory(false);
    } else if (name === 'totalAmount' || name === 'paidAmount') {
      const total = name === 'totalAmount' ? parseFloat(value) || 0 : parseFloat(formData.totalAmount) || 0;
      const paid = name === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(formData.paidAmount) || 0;
      const pending = Math.max(0, total - paid);
      const isPaid = pending === 0;
      
      setIsPaidExpense(isPaid);
      
      setFormData({
        ...formData,
        [name]: value,
        pendingAmount: pending,
        status: isPaid ? 'paid' : 'pending'
      });
    } else if (name === 'paymentMode') {
      setFormData({
        ...formData,
        [name]: value,
        status: (value === 'cash' && parseFloat(formData.paidAmount) === parseFloat(formData.totalAmount)) ? 'paid' : 'pending'
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setShowNewSubCategory(true);
      setFormData({ ...formData, subCategory: '' });
    } else {
      setShowNewSubCategory(false);
      setFormData({ ...formData, subCategory: value });
    }
    if (errors.subCategory) {
      setErrors(prev => ({ ...prev, subCategory: '' }));
    }
  };

  const handleAddSubCategory = () => {
    if (newSubCategory.trim()) {
      const category = formData.category;
      const newSub = newSubCategory.trim().toLowerCase();
      
      // Add to state
      setSubCategories(prev => ({
        ...prev,
        [category]: [...(prev[category] || []), newSub]
      }));
      
      // Update form data
      setFormData({ 
        ...formData, 
        subCategory: newSub
      });
      
      setNewSubCategory('');
      setShowNewSubCategory(false);
      
      if (errors.subCategory) {
        setErrors(prev => ({ ...prev, subCategory: '' }));
      }
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const newEntries = files.map((file) => ({ name: file.name, file }));
    setFormData({
      ...formData,
      attachments: [...formData.attachments, ...newEntries]
    });
    e.target.value = '';
  };

  const handlePartialPaymentSubmit = () => {
    if (!partialAmount || parseFloat(partialAmount) <= 0) {
      alert('Please enter a valid amount greater than 0');
      return;
    }
    
    const pending = parseFloat(formData.pendingAmount);
    const partial = parseFloat(partialAmount);
    
    if (partial > pending) {
      alert(`Partial amount cannot exceed pending amount of ₹${pending.toLocaleString()}`);
      return;
    }
    
    onSettlePayment(expense.id, 'partial', partial);
    onCancel();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('Please fix the errors in the form');
      return;
    }
    
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const currentTime = now.toLocaleTimeString('en-US', { 
      hour12: true, 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    let updatedFormData = { ...formData };
    if (formData.category === 'purchase_invoice' && !updatedFormData.subCategory) {
      updatedFormData.subCategory = 'products';
    }
    const totalAmount = parseFloat(formData.totalAmount);
    const paidAmount = parseFloat(formData.paidAmount);
    const pendingAmount = Math.max(0, totalAmount - paidAmount);
    
    // For paid expenses, we only update basic info, not payment details
    if (isPaidExpense && expense) {
      // Don't modify payment-related fields for paid expenses
      updatedFormData.totalAmount = parseFloat(expense.totalAmount);
      updatedFormData.paidAmount = parseFloat(expense.paidAmount);
      updatedFormData.pendingAmount = 0;
      updatedFormData.status = 'paid';
    } else {
      // Add transaction record if this is an update with payment settlement
      if (expense) {
        const originalPaid = parseFloat(expense.paidAmount);
        if (paidAmount > originalPaid) {
          const settlementAmount = paidAmount - originalPaid;
          const newTransaction = {
            date: currentDate,
            time: currentTime,
            amount: settlementAmount,
            type: 'payment',
            description: settlementAmount === pendingAmount ? 'Full settlement' : `Partial payment (₹${settlementAmount.toLocaleString()})`,
            paymentMode: formData.paymentMode
          };
          
          updatedFormData.transactions = [...expense.transactions, newTransaction];
        }
      } else {
        // For new expenses, add initial transaction if paid amount > 0
        if (paidAmount > 0) {
          updatedFormData.transactions = [{
            date: formData.date,
            time: currentTime,
            amount: paidAmount,
            type: 'payment',
            description: 'Initial payment',
            paymentMode: formData.paymentMode
          }];
        }
      }

      // Ensure numeric values
      updatedFormData.totalAmount = totalAmount;
      updatedFormData.paidAmount = paidAmount;
      updatedFormData.pendingAmount = pendingAmount;
      updatedFormData.status = pendingAmount === 0 ? 'paid' : 'pending';
    }

    const newFiles = (updatedFormData.attachments || []).filter((a) => a && a.file instanceof File).map((a) => a.file);
    updatedFormData.attachmentsFiles = newFiles;
    updatedFormData.newAttachments = newFiles;

    onSave(updatedFormData);
  };

  const renderPaymentModeOption = (mode) => {
    const Icon = mode.icon;
    return (
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <span>{mode.label}</span>
      </div>
    );
  };

  const renderCategoryOption = (cat) => {
    const Icon = cat.icon;
    return (
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <span>{cat.label}</span>
      </div>
    );
  };

  const pendingAmount = parseFloat(formData.totalAmount || 0) - parseFloat(formData.paidAmount || 0);

  return (
    <form onSubmit={handleSubmit} className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">
            {expense ? 'Edit Expense' : 'Add New Expense'}
          </h3>
          <p className="text-gray-500 mt-1">
            {expense ? (isPaidExpense ? 'View and edit basic information (Payment cannot be modified)' : 'Update expense details and settle payments') : 'Fill in the expense details below'}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-6 h-6 text-gray-500 hover:text-gray-700" />
        </button>
      </div>

      {/* Partial Payment Section (only when editing and has pending amount) */}
      {expense && !isPaidExpense && expense.pendingAmount > 0 && !showPartialPayment && (
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Info className="w-5 h-5 text-blue-600" />
              <h4 className="text-lg font-semibold text-gray-900">Settle Payment</h4>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-red-600">
                ₹{expense.pendingAmount.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Pending amount</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Are you sure you want to settle the full pending amount of ₹${expense.pendingAmount.toLocaleString()}?`)) {
                  onSettlePayment(expense.id, 'full', expense.pendingAmount);
                  onCancel();
                }
              }}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all text-sm font-medium"
            >
              Full Settlement
            </button>
            <button
              type="button"
              onClick={() => setShowPartialPayment(true)}
              className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-cyan-700 transition-all text-sm font-medium"
            >
              Partial Payment
            </button>
          </div>
        </div>
      )}

      {showPartialPayment && (
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Make Partial Payment</h4>
            <button
              type="button"
              onClick={() => setShowPartialPayment(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600 mb-2">Payment Summary</div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-500">Total</div>
                  <div className="font-semibold">₹{expense?.totalAmount.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Already Paid</div>
                  <div className="font-semibold text-green-600">₹{expense?.paidAmount.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Remaining</div>
                  <div className="font-semibold text-red-600">₹{expense?.pendingAmount.toLocaleString()}</div>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount to Pay (Max: ₹{expense?.pendingAmount.toLocaleString()})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                <input
                  type="number"
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  min="0"
                  max={expense?.pendingAmount}
                  step="0.01"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter amount"
                />
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Remaining after payment: ₹{(expense?.pendingAmount - (parseFloat(partialAmount) || 0)).toLocaleString()}
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowPartialPayment(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-all text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePartialPaymentSubmit}
                className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-cyan-700 transition-all text-sm font-medium"
              >
                Submit Payment
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Date *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Category *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {categories.map(cat => (
                <label
                  key={cat.value}
                  className={`relative cursor-pointer ${formData.category === cat.value ? 'ring-2 ring-green-500' : ''}`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={formData.category === cat.value}
                    onChange={handleChange}
                    className="sr-only"
                    required
                    disabled={isPaidExpense && expense}
                  />
                  <div className={`p-4 rounded-xl border transition-all ${
                    formData.category === cat.value 
                      ? 'bg-green-50 border-green-500' 
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  } ${(isPaidExpense && expense) ? 'opacity-70 cursor-not-allowed' : ''}`}>
                    {renderCategoryOption(cat)}
                  </div>
                </label>
              ))}
            </div>
            {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
            {isPaidExpense && expense && (
              <p className="text-xs text-gray-500 mt-2">Category cannot be changed for paid expenses</p>
            )}
          </div>

          {/* Sub Category (hidden for Purchase Invoice) */}
          {formData.category && formData.category !== 'purchase_invoice' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Sub Category *
              </label>
              <div className="space-y-3">
                <select
                  value={formData.subCategory}
                  onChange={handleSubCategoryChange}
                  required
                  disabled={isPaidExpense && expense}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.subCategory ? 'border-red-500' : 'border-gray-300'
                  } ${(isPaidExpense && expense) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select Sub Category</option>
                  {subCategories[formData.category]?.map(sub => (
                    <option key={sub} value={sub}>
                      {sub.charAt(0).toUpperCase() + sub.slice(1)}
                    </option>
                  ))}
                  <option value="add_new">+ Add New Sub Category</option>
                </select>

                {showNewSubCategory && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newSubCategory}
                      onChange={(e) => setNewSubCategory(e.target.value)}
                      placeholder="Enter new sub category"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl"
                    />
                    <button
                      type="button"
                      onClick={handleAddSubCategory}
                      className="bg-green-500 text-white px-4 py-3 rounded-xl hover:bg-green-600"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              {errors.subCategory && <p className="mt-1 text-sm text-red-600">{errors.subCategory}</p>}
            </div>
          )}

          {/* Products (Purchase Invoice only) */}
          {formData.category === 'purchase_invoice' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Products
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-xl p-3 bg-gray-50">
                {products.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">No products added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {products.map((product) => {
                      const isChecked = (formData.selectedProductIds || []).includes(product.id);
                      return (
                        <label
                          key={product.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-white cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isPaidExpense && expense}
                            onChange={() => {
                              const ids = formData.selectedProductIds || [];
                              const next = isChecked
                                ? ids.filter((id) => id !== product.id)
                                : [...ids, product.id];
                              setFormData({ ...formData, selectedProductIds: next });
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm font-medium text-gray-900">{product.name}</span>
                          {product.price != null && (
                            <span className="text-xs text-gray-500">₹{Number(product.price).toLocaleString()}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              {(formData.selectedProductIds || []).length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {(formData.selectedProductIds || []).length} product(s) selected
                </p>
              )}
            </div>
          )}

          {/* Vendor Name (Purchase Invoice) */}
          {formData.category === 'purchase_invoice' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Vendor Name *
              </label>
              <select
                value={formData.vendorName}
                onChange={(e) => {
                  setFormData({ ...formData, vendorName: e.target.value });
                  if (errors.vendorName) setErrors((prev) => ({ ...prev, vendorName: '' }));
                }}
                required
                disabled={isPaidExpense && expense}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.vendorName ? 'border-red-500' : 'border-gray-300'
                } ${(isPaidExpense && expense) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select vendor</option>
                {suppliers.map((v) => (
                  <option key={v.id} value={v.name || ''}>
                    {v.name || `Vendor #${v.id}`}
                  </option>
                ))}
              </select>
              {errors.vendorName && <p className="mt-1 text-sm text-red-600">{errors.vendorName}</p>}
            </div>
          )}

          {/* Staff Name (Staff Payment) */}
          {formData.category === 'staff_payment' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Staff Name *
              </label>
              <select
                value={formData.staffName}
                onChange={(e) => {
                  setFormData({ ...formData, staffName: e.target.value });
                  if (errors.staffName) setErrors((prev) => ({ ...prev, staffName: '' }));
                }}
                required
                disabled={isPaidExpense && expense}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.staffName ? 'border-red-500' : 'border-gray-300'
                } ${(isPaidExpense && expense) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select staff</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.name || ''}>
                    {s.name || s.employeeId || `Staff #${s.id}`}
                  </option>
                ))}
              </select>
              {errors.staffName && <p className="mt-1 text-sm text-red-600">{errors.staffName}</p>}
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter expense description"
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Payment Mode *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {paymentModes.map(mode => (
                <label
                  key={mode.value}
                  className={`relative cursor-pointer ${formData.paymentMode === mode.value ? 'ring-2 ring-green-500' : ''} ${
                    (isPaidExpense && expense) ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMode"
                    value={mode.value}
                    checked={formData.paymentMode === mode.value}
                    onChange={handleChange}
                    className="sr-only"
                    required
                    disabled={isPaidExpense && expense}
                  />
                  <div className={`p-3 rounded-xl border transition-all ${
                    formData.paymentMode === mode.value 
                      ? 'bg-green-50 border-green-500' 
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  } ${(isPaidExpense && expense) ? 'cursor-not-allowed' : ''}`}>
                    {renderPaymentModeOption(mode)}
                  </div>
                </label>
              ))}
            </div>
            {errors.paymentMode && <p className="mt-1 text-sm text-red-600">{errors.paymentMode}</p>}
            {isPaidExpense && expense && (
              <p className="text-xs text-gray-500 mt-2">Payment mode cannot be changed for paid expenses</p>
            )}
          </div>

          {/* Amount Fields - Only editable for pending expenses or new expenses */}
          {!isPaidExpense || !expense ? (
            <div className="bg-gray-50 rounded-xl p-5">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Amount Details *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Total Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      name="totalAmount"
                      value={formData.totalAmount}
                      onChange={handleChange}
                      required
                      min="0"
                      step="0.01"
                      className={`w-full pl-8 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        errors.totalAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.totalAmount && <p className="mt-1 text-sm text-red-600">{errors.totalAmount}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Paid Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      name="paidAmount"
                      value={formData.paidAmount}
                      onChange={handleChange}
                      required
                      min="0"
                      max={formData.totalAmount || undefined}
                      step="0.01"
                      className={`w-full pl-8 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        errors.paidAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.paidAmount && <p className="mt-1 text-sm text-red-600">{errors.paidAmount}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Pending Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      value={pendingAmount.toFixed(2)}
                      readOnly
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-100"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
              
              {/* Status Display */}
              <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    pendingAmount === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {pendingAmount === 0 ? 'Paid' : 'Pending'}
                  </span>
                </div>
                {pendingAmount > 0 && (
                  <div className="text-xs text-gray-500 mt-2">
                    ₹{pendingAmount.toLocaleString()} remaining to pay
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Display only mode for paid expenses */
            <div className="bg-gray-50 rounded-xl p-5">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Payment Information (Read Only)
              </label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="font-bold text-gray-900">
                    ₹{parseFloat(formData.totalAmount).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Paid Amount</span>
                  <span className="font-semibold text-green-600">
                    ₹{parseFloat(formData.paidAmount).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Pending Amount</span>
                  <span className="font-semibold text-green-600">
                    ₹{parseFloat(formData.pendingAmount).toLocaleString()}
                  </span>
                </div>
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Fully Paid
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    This expense has been fully paid and payment details cannot be modified
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Upload Receipt/Screenshot (Optional)
            </label>
            <div className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  <label className="relative cursor-pointer font-medium text-green-600 hover:text-green-500">
                    <span>Click to upload</span>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="sr-only"
                      accept="image/*,.pdf,.doc,.docx"
                    />
                  </label>
                  {' '}or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG, PDF up to 10MB
                </p>
              </div>
            </div>
          </div>

          {/* Attachments Preview */}
          {formData.attachments.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Attachments ({formData.attachments.length})
              </label>
              <div className="space-y-2">
                {formData.attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">
                        <File className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                        {typeof file === 'object' && file !== null ? file.name : file}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newAttachments = [...formData.attachments];
                        newAttachments.splice(index, 1);
                        setFormData({ ...formData, attachments: newAttachments });
                      }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-500 hover:text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="mt-10 flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-8 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-3 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg transform hover:-translate-y-1 font-semibold"
        >
          {expense ? (isPaidExpense ? 'Update Basic Info' : 'Update Expense') : 'Add Expense'}
        </button>
      </div>
    </form>
  );
};

export default ExpenseForm;